import React, {useContext, useEffect, useRef, useState} from 'react';
import {Platform} from 'react-native';
import {OrdersContext} from "./OrdersContext";
import {CurrentScreenContext} from "./CurrentScreenContext";
import NewOrdersNotificationModal from '../components/NewOrdersNotificationModal';
import OrderCancellationModal from '../components/OrderCancellationModal';
import AsyncStorage from '@react-native-async-storage/async-storage';

const createSoundPlayer = () => {
  if (Platform.OS === 'android') {
    try {
      // React Native Sound for Android
      const Sound = require('react-native-sound');
      Sound.setCategory('Playback');

      return {
        play: (soundPath, volume = 1.0) => {
          return new Promise((resolve, reject) => {
            const sound = new Sound(soundPath, (error) => {
              if (error) {
                console.error('Failed to load the sound', error);
                reject(error);
                return;
              }

              sound.setVolume(volume);
              sound.play((success) => {
                if (!success) {
                  console.error('Sound did not play successfully');
                }
                resolve(sound);
              });
            });
          });
        },
        stop: (soundInstance) => {
          if (soundInstance) {
            soundInstance.stop();
            soundInstance.release();
          }
        }
      };
    } catch (error) {
      console.warn('Failed to load react-native-sound', error);
      // Fallback to web audio if sound library fails
      return createWebSoundPlayer();
    }
  } else {
    // Web Audio API for web and other platforms
    return createWebSoundPlayer();
  }
};

// Web Audio API implementation
const createWebSoundPlayer = () => {
  const safeCloseAudioContext = (audioContext) => {
    if (!audioContext || typeof audioContext.close !== 'function') {
      return;
    }

    if (audioContext.state === 'closed') {
      return;
    }

    // Prevent unhandled promise rejections when close is called during teardown races.
    return audioContext.close().catch((error) => {
      if (error?.name !== 'InvalidStateError') {
        console.error('Error closing audio context:', error);
      }
    });
  };

  return {
    play: (soundPath, volume = 1.0) => {
      return new Promise((resolve, reject) => {
        // Create audio context
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();

        // Fetch the audio file
        fetch(soundPath)
            .then(response => response.arrayBuffer())
            .then(arrayBuffer => audioContext.decodeAudioData(arrayBuffer))
            .then(audioBuffer => {
              // Create source
              const source = audioContext.createBufferSource();
              source.buffer = audioBuffer;

              // Create gain node for volume control
              const gainNode = audioContext.createGain();
              gainNode.gain.setValueAtTime(volume, audioContext.currentTime);

              // Connect nodes
              source.connect(gainNode);
              gainNode.connect(audioContext.destination);

              // Play the sound
              source.start(0);

              const audioInstance = {
                source,
                context: audioContext,
                __isStopped: false,
                __closePromise: null,
              };

              // Handle sound completion
              source.onended = () => {
                audioInstance.__isStopped = true;
                if (!audioInstance.__closePromise) {
                  audioInstance.__closePromise = safeCloseAudioContext(audioContext);
                }
              };

              // Return the source for potential stopping
              resolve(audioInstance);
            })
            .catch(error => {
              safeCloseAudioContext(audioContext);
              console.error('Error playing sound:', error);
              reject(error);
            });
      });
    },
    stop: (audioInstance) => {
      if (audioInstance && audioInstance.source) {
        if (audioInstance.__isStopped) {
          return;
        }

        audioInstance.__isStopped = true;

        try {
          audioInstance.source.stop();
        } catch (error) {
          if (error?.name !== 'InvalidStateError') {
            console.error('Error stopping sound:', error);
          }
        }

        if (audioInstance.context && !audioInstance.__closePromise) {
          audioInstance.__closePromise = safeCloseAudioContext(audioInstance.context);
        }
      }
    }
  };
};

const OrderNotificationService = () => {
  const { orders } = useContext(OrdersContext);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancelledOrder, setCancelledOrder] = useState(null);
  const soundPlayerRef = useRef(null);
  const intervalRef = useRef(null);
  const { currentScreen } = useContext(CurrentScreenContext);
  const previousPendingCountRef = useRef(0);
  const previousOrdersRef = useRef([]);
  const shownCancellationsRef = useRef([]);

  // Notification sound options
  const notificationSounds = Platform.select({
    android: {
      Default: require('../../public/sounds/default.mp3'),
      Alarm: require('../../public/sounds/alarm.mp3'),
      Clock: require('../../public/sounds/clock.mp3'),
      Ringtone:  require('../../public/sounds/ringtone.mp3'),
      SciFi: require('../../public/sounds/sci-fi.wav'),
      Doorbell: require('../../public/sounds/doorbell.mp3'),
    },
    web: {
      Default: require('../../public/sounds/default.mp3'),
      Alarm: require('../../public/sounds/alarm.mp3'),
      Clock: require('../../public/sounds/clock.mp3'),
      Ringtone:  require('../../public/sounds/ringtone.mp3'),
      SciFi: require('../../public/sounds/sci-fi.wav'),
      Doorbell: require('../../public/sounds/doorbell.mp3'),
    },
    default: {
      Default: require('../../public/sounds/default.mp3'),
      Alarm: require('../../public/sounds/alarm.mp3'),
      Clock: require('../../public/sounds/clock.mp3'),
      Ringtone: require('../../public/sounds/ringtone.mp3'),
      SciFi: require('../../public/sounds/sci-fi.wav'),
      Doorbell: require('../../public/sounds/doorbell.mp3'),
    }
  });

  // Initialize sound player
  const soundPlayer = useRef(createSoundPlayer()).current;

  // Load shown cancellations from AsyncStorage and clean up old entries
  useEffect(() => {
    const loadShownCancellations = async () => {
      try {
        const storedCancellations = await AsyncStorage.getItem('shownCancellations');
        if (storedCancellations) {
          // Load the stored cancellations
          const cancellations = JSON.parse(storedCancellations);

          // Limit the size of the cancellations array to prevent it from growing too large
          // Keep only the most recent 100 entries
          const limitedCancellations = cancellations.slice(-100);

          shownCancellationsRef.current = limitedCancellations;
          console.log("Loaded shown cancellations:", shownCancellationsRef.current);

          // If we limited the array, update the storage
          if (limitedCancellations.length < cancellations.length) {
            await AsyncStorage.setItem('shownCancellations', JSON.stringify(limitedCancellations));
            console.log("Cleaned up shown cancellations list, removed", 
              cancellations.length - limitedCancellations.length, "old entries");
          }
        }
      } catch (error) {
        console.error("Failed to load shown cancellations:", error);
      }
    };

    loadShownCancellations();
  }, []);

  // Update pending orders
  useEffect(() => {
    if (orders?.length > 0) {
      const filteredPendingOrders = orders.filter(order => (order.status === "pending" && !order.storno)) || [];
      setPendingOrders(filteredPendingOrders);

      // Show modal if there are new pending orders
      if (filteredPendingOrders.length > previousPendingCountRef.current && filteredPendingOrders.length > 0) {
        setShowModal(true);
      }

      // Close modal if pending orders becomes 0 and modal is still open
      if (filteredPendingOrders.length === 0) {
        setShowModal(false);
      }

      // Update previous count
      previousPendingCountRef.current = filteredPendingOrders.length;
    } else {
      // No orders at all - reset pending state
      if (previousPendingCountRef.current > 0) {
        setPendingOrders([]);
        setShowModal(false);
        previousPendingCountRef.current = 0;
      }
    }
  }, [orders]);

  // Check for cancelled orders
  useEffect(() => {
    // Skip if orders haven't meaningfully changed (same IDs and statuses)
    const currentOrderKeys = orders?.map(o => `${o._id}:${o.status}`).sort().join(',') || '';
    const previousOrderKeys = previousOrdersRef.current?.map(o => `${o._id}:${o.status}`).sort().join(',') || '';
    
    if (currentOrderKeys === previousOrderKeys) {
      return;
    }

    if (orders?.length > 0) {
      // Check each order to see if its status changed to cancelled
      for (const order of orders) {
        // Skip if we've already shown a cancellation modal for this order
        if (shownCancellationsRef.current.includes(order._id)) {
          continue;
        }

        // Find the previous state of this order
        const prevOrder = previousOrdersRef.current.find(o => o._id === order._id);

        // If the order existed before and its status changed to cancelled
        if (prevOrder && 
            order.status === "cancelled" && 
            (prevOrder.status === "pending" || 
             prevOrder.status === "preparing" || 
             prevOrder.status === "busy" || 
             prevOrder.status === "accepted")) {

          console.log(`Detected cancelled order: ${order.code || order._id}`);

          // Set the cancelled order and show the modal
          setCancelledOrder(order);
          setShowCancellationModal(true);
          break; // Only show one cancellation at a time
        }

        // Also check for orders that are already cancelled but haven't been shown yet
        // This helps when the app first loads or when orders are added directly with cancelled status
        if (order.status === "cancelled" && !prevOrder && !cancelledOrder) {
          // Check if this order was created recently (within the last hour)
          const orderTime = new Date(order.createdAt || Date.now());
          const currentTime = new Date();
          const timeDifference = (currentTime - orderTime) / (1000 * 60); // difference in minutes

          if (timeDifference < 60) { // Only show cancellation modal for recent orders (last hour)
            console.log(`Showing cancellation modal for recent order: ${order.code || order._id}`);
            setCancelledOrder(order);
            setShowCancellationModal(true);
            break;
          }
        }
      }
    }

    // Update the previous orders reference
    previousOrdersRef.current = orders ? [...orders] : [];
  }, [orders]);

  // Function to play sound
  const playNotificationSound = async () => {
    if (currentScreen === 'OrderDetailsScreen' || currentScreen === 'NotificationsScreen') {
      console.log(`Sound suppressed on ${currentScreen}`);
      // Stop sound if it's playing and we're on a screen that should suppress sound
      if (soundPlayerRef.current) {
        soundPlayer.stop(soundPlayerRef.current);
        soundPlayerRef.current = null;
      }
      return;
    }
    try {
      // Stop previous sound if playing
      if (soundPlayerRef.current) {
        soundPlayer.stop(soundPlayerRef.current);
      }

      // Play new sound
      soundPlayerRef.current = await soundPlayer.play(
          notificationSounds.Default,
          1.0
      );
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  };

  // Sound interval logic
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (pendingOrders.length > 0) {
      // Play sound immediately
      playNotificationSound();

      // Set up interval to repeat sound
      intervalRef.current = setInterval(playNotificationSound, 7500);
    }

    // Cleanup function
    return () => {
      // Clear interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Release sound resource
      if (soundPlayerRef.current) {
        soundPlayer.stop(soundPlayerRef.current);
      }
    };
  }, [pendingOrders]);

  // Watch for screen changes to stop sound when navigating to NotificationsScreen
  useEffect(() => {
    if (currentScreen === 'NotificationsScreen' || currentScreen === 'OrderDetailsScreen') {
      // Stop sound if it's playing
      if (soundPlayerRef.current) {
        soundPlayer.stop(soundPlayerRef.current);
        soundPlayerRef.current = null;
      }

      // Clear interval to prevent new sounds from playing
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } else if (pendingOrders.length > 0 && !intervalRef.current) {
      // Restart interval if we navigate away from NotificationsScreen and still have pending orders
      playNotificationSound();
      intervalRef.current = setInterval(playNotificationSound, 7500);
    }
  }, [currentScreen, pendingOrders.length]);

  // Return the notification modals
  return (
    <>
      <NewOrdersNotificationModal
        visible={showModal}
        onClose={() => setShowModal(false)}
        pendingOrdersCount={pendingOrders.length}
      />
      {cancelledOrder && (
        <OrderCancellationModal
          visible={showCancellationModal}
          onClose={() => {
            console.log("Closing cancellation modal");
            setShowCancellationModal(false);

            // Add the order to the shown cancellations list
            if (cancelledOrder && cancelledOrder._id) {
              // Update the in-memory tracking state
              shownCancellationsRef.current = [...shownCancellationsRef.current, cancelledOrder._id];

              // Persist to AsyncStorage
              AsyncStorage.setItem('shownCancellations', JSON.stringify(shownCancellationsRef.current))
                .then(() => {
                  console.log(`Added order ${cancelledOrder.code || cancelledOrder._id} to shown cancellations`);
                })
                .catch(error => {
                  console.error("Failed to save shown cancellations:", error);
                });
            }

            // Reset the cancelled order after a short delay to allow the modal to close smoothly
            setTimeout(() => {
              setCancelledOrder(null);
              console.log("Cancelled order state reset");
            }, 500);
          }}
          order={cancelledOrder}
        />
      )}
    </>
  );
};

export default OrderNotificationService;
